import { useEffect, useMemo, useState } from "react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getLocalizedDate } from "../../../functions/localization";

import { registerPoppins } from "../../../fonts/registerFonts";
import PDFHeaderControls from "./PDFHeaderControls";
import PDFPagination from "./PDFPagination";
import PDFReportDocument from "./PDFReportDocument";

registerPoppins();

const MAX_PREVIEW_ROWS = 10;

const MyView = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 20px;
  height: 100%;
  max-height: 1024px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 75px;
  min-height: 75px;
  border-bottom: 1px solid #a7bbcf;
`;

const PDFReportViewer = ({
  user,
  title,
  headings,
  groupedField,
  data,
  columnSums,
  columnAverages,
  selectionRange,
  onBack,
  showAVGRow,
  showSUMRow,
  headingsSpacing,
  dataSpacing,
  defaultFontSize,
}) => {
  const { t } = useTranslation("pdfReportViewer");

  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [columnWidthTypeRadioValue, setColumnWidthTypeRadioValue] = useState(1);
  const [fontSizeRadioValue, setFontSizeRadioValue] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 2 : 1,
  );

  const [headingsAndDataFontSize, setHeadingsAndDataFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 8 : 10,
  );
  const [sumAndAvgFontSize, setSumAndAvgFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 10 : 12,
  );

  useEffect(() => {
    if (fontSizeRadioValue === 1) {
      setHeadingsAndDataFontSize(10);
      setSumAndAvgFontSize(12);
    } else {
      setHeadingsAndDataFontSize(8);
      setSumAndAvgFontSize(10);
    }
  }, [fontSizeRadioValue]);

  const handleIsLoading = (boolVal) => {
    setIsLoading(boolVal);
  };

  const allowCustomWidths =
    headingsSpacing?.length === headings.length &&
    dataSpacing?.length === headings.length;

  useEffect(() => {
    if (allowCustomWidths) setColumnWidthTypeRadioValue(3);
  }, []);

  const charWidthInPx = 6.4;
  const pageWidth = 760;
  const columnWidth = pageWidth / headings.length;

  // Compute column widths based on content
  const variableColumnWidthsArray = useMemo(() => {
    const tempWidths = headings.map((heading) => {
      const parts = heading.trim().split(" ");
      const maxLength = Math.max(...parts.map((part) => part.length));
      return maxLength * charWidthInPx;
    });

    const tableRowsData =
      !groupedField || groupedField === "No grouping" ? data : data;

    tableRowsData.forEach((row) =>
      row.forEach((val, index) => {
        const str =
          typeof val === "string" ? val.substring(0, 750) : val.toString();
        str.split("\n").forEach((s) => {
          const width = Math.min(s.length * charWidthInPx, 200);
          if (width > tempWidths[index]) {
            tempWidths[index] = width;
          }
        });
      }),
    );

    const totalWidth = tempWidths.reduce((sum, w) => sum + w, 0);
    const factor = 758 / totalWidth;
    return tempWidths.map((w) => w * factor);
  }, [headings, data, groupedField]);

  const previewData = useMemo(
    () =>
      data.slice(
        currentPage * MAX_PREVIEW_ROWS,
        (currentPage + 1) * MAX_PREVIEW_ROWS,
      ),
    [data, currentPage],
  );

  const startDate = selectionRange?.startDate
    ? getLocalizedDate(
        selectionRange.startDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;
  const endDate = selectionRange?.endDate
    ? getLocalizedDate(
        selectionRange.endDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;

  const createDocument = (dataSet, isDownload = false) => (
    <PDFReportDocument
      title={title}
      headings={headings}
      data={dataSet}
      columnWidth={columnWidth}
      fontSize={fontSizeRadioValue === 2 ? 8 : 10}
      startDate={startDate}
      endDate={endDate}
      variableColumnWidthsArray={variableColumnWidthsArray}
      columnWidthTypeRadioValue={columnWidthTypeRadioValue}
      headingsAndDataFontSize={headingsAndDataFontSize}
      sumAndAvgFontSize={sumAndAvgFontSize}
      columnSums={columnSums}
      columnAverages={columnAverages}
      showSUMRow={showSUMRow}
      showAVGRow={showAVGRow}
      headingsSpacing={headingsSpacing}
      dataSpacing={dataSpacing}
      isDownload={isDownload}
    />
  );

  const handleDownload = () => {
    setIsLoading(true);

    // Defer to next event loop tick to allow spinner to render
    setTimeout(async () => {
      try {
        const doc = createDocument(data, true);
        const blobGenerator = pdf([]);
        blobGenerator.updateContainer(doc);
        const blob = await blobGenerator.toBlob();
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `${title.replace(/\s+/g, "_")}_Report.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        setIsLoading(false);
      }
    }, 0); // ← allow render before blocking
  };

  return (
    <MyView>
      <Header>
        <PDFHeaderControls
          t={t}
          title={title}
          onBack={onBack}
          allowCustomWidths={allowCustomWidths}
          columnWidthTypeRadioValue={columnWidthTypeRadioValue}
          setColumnWidthTypeRadioValue={setColumnWidthTypeRadioValue}
          fontSizeRadioValue={fontSizeRadioValue}
          setFontSizeRadioValue={setFontSizeRadioValue}
          onDownload={handleDownload}
          isLoading={isLoading}
          onIsLoading={handleIsLoading}
        />
      </Header>

      <PDFViewer width="100%" height="86%">
        {createDocument(previewData)}
      </PDFViewer>

      <PDFPagination
        totalItems={data.length}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </MyView>
  );
};

export default PDFReportViewer;
